import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './App.css';


export default function Technology() {
    useEffect(() => {
        window.scrollTo(0, 0)
        document.title = 'QRmor Technology — Non-Clonable, Anti-Counterfeit QR Code Security';
        const metaDesc = document.querySelector('meta[name="description"]');
        const content = 'How QRmor works: non-clonable, secure QR code technology built for anti-counterfeit product authentication, genuine QR code verification, and pharma compliance.';
        if (metaDesc) {
            metaDesc.setAttribute('content', content);
        } else {
            const tag = document.createElement('meta');
            tag.name = 'description';
            tag.content = content;
            document.head.appendChild(tag);
        }
    }, []);

    return (
        <>
            <header>
                <nav className="wrap">
                    <div className="brand">
                        <img src="/assets/QRmor.png" alt="QRmor logo" />
                    </div>
                    <Link to="/" className="btn btn-primary btn-sm">Back to home</Link>
                </nav>
            </header>

            <section>
                <div className="wrap legal-content" style={{ maxWidth: 860, padding: '48px 32px 80px' }}>
                    <span className="eyebrow">Technology</span>
                    <h1 style={{ fontSize: 'clamp(28px, 4vw, 40px)', marginTop: 12, marginBottom: 20 }}>
                        The Technology behind QRmor's Anti-Counterfeit Non-Clonable QR code security
                    </h1>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
                        <div>
                            <p style={{ color: 'var(--ink-soft)', fontSize: 16, lineHeight: 1.7 }}>
                                A standard QR code is just a container for data — anyone with a photo of it can
                                reproduce it exactly, which is why ordinary QR codes offer no real protection
                                against counterfeiting. QRmor takes a different approach: it treats every QR
                                code as something that must prove it is a genuine QR code, not merely a scannable
                                one. This is the foundation of what makes a QRmor code non-clonable rather than
                                just unique.
                            </p>
                        </div>

                        <div>
                            <h3>From QR code to secure QR</h3>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.7 }}>
                                A secure QR code needs to do more than encode a link or a batch number — it
                                needs to resist duplication. QRmor's anti-counterfeit design means that when the
                                underlying code is copied, printed again, or scanned from an image rather than
                                the original surface, the system is able to distinguish that duplicate from the
                                genuine QR code it was cloned from. The result is flagged instantly, rather than
                                validating as if it were authentic.
                            </p>
                        </div>

                        <div>
                            <h3>Why non-clonable matters</h3>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.7 }}>
                                Counterfeiters rely on the assumption that a QR code, once photographed, can be
                                reprinted indefinitely on fake packaging. A non-clonable QR code breaks that
                                assumption. Because each QRmor code carries authentication characteristics that
                                a straightforward image copy cannot reproduce, duplication becomes far harder to
                                pull off at scale — which is what separates true anti-counterfeit protection from
                                simple product serialization.
                            </p>
                        </div>

                        <div>
                            <h3>Security built for regulated industries</h3>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.7 }}>
                                Security requirements are highest where counterfeit products cause the most
                                harm. In pharma, a fake medicine isn't just a lost sale — it's a patient safety
                                risk. QRmor's approach to secure QR authentication was built with this in mind,
                                aligning with GAMP and 21 CFR Part 11 guideline expectations so that pharma
                                manufacturers can adopt genuine QR code verification without reworking their
                                existing compliance processes.
                            </p>
                        </div>

                        <div>
                            <h3>Verification, not just detection</h3>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.7 }}>
                                Anti-counterfeit technology is only useful if it produces a clear answer. Every
                                scan of a QRmor code returns a straightforward result — genuine or flagged — along
                                with a timestamp and, where available, a location. This turns each individual
                                secure QR interaction into a data point brands can use to see where counterfeit
                                activity is emerging, not just whether a single code passed or failed.
                            </p>
                        </div>

                        <div>
                            <h3>Where this applies</h3>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.7 }}>
                                While pharma is the clearest case for non-clonable, anti-counterfeit QR code
                                security, the same underlying technology extends to agritech, alcohol and
                                beverages, automobiles, luxury goods, and any product category where a genuine
                                QR code needs to be trusted the same way, every time it's scanned.
                            </p>
                        </div>
                        <div>
                            <h3>Conditions That Affect Scan Reliability</h3>
                            <p style={{ color: 'var(--ink-soft)', marginTop: 8, lineHeight: 1.7 }}>
                                QRmor is designed to work reliably across everyday smartphones in real-world,
                                uncontrolled environments — not just in a lab. As with any authentication technology
                                that depends on a smartphone camera and an end user's scanning conditions, certain
                                factors can affect scan accuracy or readability. These are common considerations
                                across all camera-based, public-facing authentication systems, not unique to QRmor:
                            </p>

                            <ul style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10, color: 'var(--ink-soft)', fontSize: 15, lineHeight: 1.6, paddingLeft: 20 }}>
                                <li><b>Device capability</b> — low-end smartphones with lower-resolution cameras or older sensors may take longer to focus or capture a clean scan.</li>
                                <li><b>Camera hardware alterations</b> — if a device's original camera lens has been replaced with a non-standard or aftermarket part, scan accuracy may be affected.</li>
                                <li><b>Lighting conditions</b> — very dim lighting, harsh direct light, glare, or uneven shadows across the code can make the pattern harder to read.</li>
                                <li><b>Camera stability</b> — a shaky hand or motion blur during the scan can prevent the camera from capturing a clear image.</li>
                                <li><b>Scan distance</b> — holding the phone too far or too close to the code can cause the camera to lose focus or crop out part of the pattern.</li>
                                <li><b>Lens cleanliness</b> — dirt, smudges, or a scratched camera lens can degrade image clarity during capture.</li>
                                <li><b>Network connectivity</b> — since verification requires a round-trip to the authentication server, a slow or unstable internet connection can delay or interrupt the result.</li>
                                <li><b>Operating system version</b> — devices running older Android versions (below Android 12) or outdated browser/webview components may not fully support the camera and scanning APIs QRmor relies on.</li>
                                <li><b>Print or surface tampering</b> — a code that has been physically damaged, scratched, torn, or deliberately tampered with after printing may not scan correctly, regardless of authenticity.</li>
                                <li><b>Print quality and substrate</b> — low-resolution printing, ink bleed, low-contrast color combinations, or printing on reflective, curved, or textured packaging surfaces can reduce scan reliability.</li>
                                <li><b>Code aging</b> — prolonged exposure to sunlight, moisture, or handling over the product's shelf life can fade or degrade the printed code over time.</li>
                                <li><b>Packaging film glare</b> — codes printed under glossy laminate, shrink-wrap, or plastic packaging film can produce glare or reflections that interfere with scanning.</li>
                                <li><b>Disproportionate cloned codes</b> — a cloned or copied QRmor code printed at a size significantly different from the original's intended dimensions may behave differently from a standard scan and should still be independently verified.</li>
                                <li><b>App or browser permissions</b> — if camera access is not granted, or is restricted by device settings, scanning cannot proceed until permissions are enabled.</li>
                            </ul>

                            <p style={{ color: 'var(--ink-soft)', marginTop: 16, lineHeight: 1.7 }}>
                                QRmor's authentication engine is built to tolerate a wide range of everyday scanning
                                conditions, and most of these factors only cause a temporary failed scan rather than
                                an incorrect result. Where a scan cannot be completed reliably, we recommend
                                re-scanning under better lighting, at a steady distance, or trying an alternate
                                device before reporting a suspected counterfeit.
                            </p>
                        </div>
                    </div>
                    <div style={{ marginTop: 32 }}>
                        <p style={{ color: 'var(--ink-soft)', lineHeight: 1.7 }}>
                            Curious about the terminology? See our{' '}
                            <Link to="/glossary" style={{ color: 'var(--ink)', textDecoration: 'underline' }}>
                                glossary of anti-counterfeit and non-clonable QR terms
                            </Link>{' '}
                            for a full breakdown of what non-clonable, clone detection, and track-and-trace actually mean.
                        </p>
                    </div>
                </div>
            </section>
        </>
    );
}